import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const BBOX_CRS = 'http://www.opengis.net/def/crs/OGC/1.3/CRS84';

export interface OgcFeatureCollection {
  type: string;
  features: OgcFeature[];
  numberMatched?: number;
  numberReturned?: number;
}

export interface OgcFeature {
  type: string;
  id?: string;
  geometry?: unknown;
  properties?: Record<string, unknown>;
}

/** OGC API - Features link object (within collection) */
export interface OgcCollectionLink {
  href?: string;
  rel?: string;
  type?: string;
  title?: string;
}

/** OGC API - Features collection object (within collections array) */
export interface OgcCollection {
  id?: string;
  links?: OgcCollectionLink[];
  [key: string]: unknown;
}

/** OGC API - Features collections list response (GET /collections) */
export interface OgcCollectionsResponse {
  links?: OgcCollectionLink[];
  collections?: OgcCollection[];
}

/**
 * Fetches OGC API - Features (OGC API Collections Items) by bounding box.
 * URL: {base}/collections/{collectionId}/items?bbox=minLng,minLat,maxLng,maxLat&bbox-crs=...
 */
@Injectable({ providedIn: 'root' })
export class OgcFeatureApiService {
  private http = inject(HttpClient);

  /**
   * Fetch OGC collections list (GET /collections).
   * Call this first before bbox queries to validate server connectivity.
   * @param serverBaseUrl - Optional override for dataset-specific OGC server
   * @param accessToken - Optional Bearer token
   */
  getCollections(
    serverBaseUrl?: string,
    accessToken?: string
  ): Promise<OgcCollectionsResponse> {
    const base = this.normalizeBaseUrl(serverBaseUrl ?? this.getBaseUrl());
    if (!base) {
      return Promise.reject(new Error('OGC server URL is not configured'));
    }
    const url = `${base}/collections`;
    let headers = new HttpHeaders({ Accept: 'application/json, text/plain, */*' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const obs = this.http.get<OgcCollectionsResponse>(url, { headers });
    return firstValueFrom(obs);
  }

  /**
   * Find collection by dataset ID and extract items endpoint href (rel="items").
   * Used to get the asset/items API base path for bbox queries.
   * @returns The items URL or null if not found
   */
  getItemsHrefFromCollections(
    collectionsResponse: OgcCollectionsResponse,
    datasetId: string
  ): string | null {
    const collections = collectionsResponse?.collections;
    if (!Array.isArray(collections) || !datasetId) return null;

    const matched = collections.find(
      (c) => String(c?.id ?? '').toLowerCase() === String(datasetId).toLowerCase()
    );
    if (!matched?.links || !Array.isArray(matched.links)) return null;

    const itemsLink = matched.links.find(
      (l) => String(l?.rel ?? '').toLowerCase() === 'items'
    );
    const href = itemsLink?.href?.trim();
    return href || null;
  }

  /**
   * From a /collections response, find the collection matching datasetId and then
   * locate the link whose type is application/geopackage+sqlite3.
   * Returns the matching href, or null if not found.
   */
  getGeopackageHrefFromCollections(
    collectionsResponse: OgcCollectionsResponse,
    datasetId: string
  ): string | null {
    const collections = collectionsResponse?.collections;
    if (!Array.isArray(collections) || !datasetId) return null;

    const matched = collections.find(
      (c) => String(c?.id ?? '').toLowerCase() === String(datasetId).toLowerCase()
    );
    if (!matched?.links || !Array.isArray(matched.links)) return null;

    const gpkgLink = matched.links.find(
      (l) =>
        String(l?.type ?? '').toLowerCase() === 'application/geopackage+sqlite3'
    );
    const href = gpkgLink?.href?.trim();
    return href || null;
  }

  /**
   * Fetch OGC feature items using items endpoint href (from collections response) and bbox.
   * @param itemsUrl - Full URL to items endpoint (e.g. from rel="items" link)
   * @param bbox - [minLng, minLat, maxLng, maxLat]
   * @param accessToken - Optional Bearer token
   */
  fetchItemsByItemsUrl(
    itemsUrl: string,
    bbox: [number, number, number, number],
    accessToken?: string
  ): Promise<OgcFeatureCollection> {
    const url = itemsUrl.includes('?')
      ? `${itemsUrl}&bbox=${bbox.join(',')}&bbox-crs=${encodeURIComponent(BBOX_CRS)}`
      : `${itemsUrl}?bbox=${bbox.join(',')}&bbox-crs=${encodeURIComponent(BBOX_CRS)}`;

    let headers = new HttpHeaders({ Accept: 'application/json, text/plain, */*' });
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const obs = this.http.get<OgcFeatureCollection>(url, { headers });
    return firstValueFrom(obs);
  }

  /**
   * Fetch items from OGC collection by bbox.
   * @param collectionId - Collection/dataset ID (e.g. UUID)
   * @param bbox - [minLng, minLat, maxLng, maxLat]
   * @param accessToken - Bearer token for authorization
   * @param serverBaseUrl - Optional override (e.g. dataset-specific OGC server). Defaults to environment.api_ogc_server_domain
   */
  fetchItemsByBbox(
    collectionId: string,
    bbox: [number, number, number, number],
    accessToken: string,
    serverBaseUrl?: string
  ): Promise<OgcFeatureCollection> {
    const base = this.normalizeBaseUrl(serverBaseUrl ?? this.getBaseUrl());
    if (!base) {
      return Promise.reject(new Error('OGC server URL is not configured'));
    }
    const url = `${base}/collections/${encodeURIComponent(collectionId)}/items`;
    const params = {
      bbox: bbox.join(','),
      'bbox-crs': BBOX_CRS,
    };
    const headers = new HttpHeaders({
      Accept: 'application/json, text/plain, */*',
      Authorization: `Bearer ${accessToken}`,
    });

    const obs = this.http.get<OgcFeatureCollection>(url, { params, headers });
    return firstValueFrom(obs);
  }

  /**
   * Fetch OGC feature items by bounding box (bbox object with bbox array).
   * Supports public collections (no token) and optional server override.
   * @param bbox - Object with bbox array [minLng, minLat, maxLng, maxLat]
   * @param itemId - Collection ID
   * @param accessToken - Optional Bearer token
   * @param serverBaseUrl - Optional override for dataset-specific OGC server
   */
  async getBbox(
    bbox: { bbox?: number[] },
    itemId: string,
    accessToken?: string,
    serverBaseUrl?: string
  ): Promise<OgcFeatureCollection | null> {
    try {
      const bboxString = bbox?.bbox?.length === 4 ? bbox.bbox.join(',') : '';
      const base = this.normalizeBaseUrl(serverBaseUrl ?? this.getBaseUrl());
      if (!base) return null;
      const url = `${base}/collections/${encodeURIComponent(itemId)}/items?bbox=${bboxString}&bbox-crs=${encodeURIComponent(BBOX_CRS)}`;

      let headers = new HttpHeaders({ Accept: 'application/json, text/plain, */*' });
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }

      const obs = this.http.get<OgcFeatureCollection>(url, { headers });
      return await firstValueFrom(obs);
    } catch {
      return null;
    }
  }

  /**
   * Fetch OGC collection metadata by ID (includes links for GPKG download).
   * @param itemId - Collection ID
   * @param accessToken - Optional Bearer token
   * @param serverBaseUrl - Optional override for dataset-specific OGC server
   */
  async getLink(
    itemId: string,
    accessToken?: string,
    serverBaseUrl?: string
  ): Promise<Record<string, unknown> | null> {
    try {
      const base = this.normalizeBaseUrl(serverBaseUrl ?? this.getBaseUrl());
      if (!base) return null;
      const url = `${base}/collections/${encodeURIComponent(itemId)}`;

      let headers = new HttpHeaders({ Accept: 'application/json, text/plain, */*' });
      if (accessToken) {
        headers = headers.set('Authorization', `Bearer ${accessToken}`);
      }

      const obs = this.http.get<Record<string, unknown>>(url, { headers });
      return await firstValueFrom(obs);
    } catch {
      return null;
    }
  }

  /**
   * Fetch blob data from a URL (e.g. for GPKG download from collection links).
   * @param url - Full URL to fetch
   * @param accessToken - Optional Bearer token
   */
  async fetchBlobFromUrl(
    url: string,
    accessToken?: string
  ): Promise<Blob> {
    let headers = new HttpHeaders();
    if (accessToken) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }
    const obs = this.http.get(url, { responseType: 'blob', headers });
    return firstValueFrom(obs);
  }

  private getBaseUrl(): string {
    return String(environment.api_ogc_server_domain ?? '').trim();
  }

  /** Normalize base URL: ensure absolute URL (add https:// if missing) and no trailing slash */
  private normalizeBaseUrl(url: string): string {
    let t = String(url ?? '').trim();
    if (!t) return '';
    // Prepend https:// if URL has no protocol (avoids resolving as relative path under localhost)
    if (!/^https?:\/\//i.test(t)) {
      t = 'https://' + t;
    }
    return t.replace(/\/+$/, '');
  }
}
